import Radio from "@mui/material/Radio";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import isString from "lodash/isString";
import React from "react";
import { EMPTY_VALUE_DASH } from "../../../helpers/constants";
import { commonSiteCoreARIALabelKeys } from "../../../helpers/constants/siteCoreKeys";
import MuiEllipseLabel from "../../ellipseLabel/MuiEllipseLabel";
import { MuiTypography } from "../../typography/MuiTypography";
import { hasMultipleNestedArrays } from "../helper";
import { Column, RowValue } from "../MuiTable";

// Add appropriate types for your props
interface TableBodyComponentProps {
  pageId: string;
  rowsPerPage: number;
  filteredResults: Record<string, RowValue>[];
  page: number;
  isGroupView?: boolean;
  selectedId?: string | null;
  selectedIdKey: string;
  handleRowSelected: (row: any) => void;
  columns: Column[];
  isShowDashIfValueEmpty: boolean;
  isLastColumnSticky: boolean;
  commonARIALabel?: Record<keyof typeof commonSiteCoreARIALabelKeys, string>;
}

const MuiTableBody: React.FC<TableBodyComponentProps> = ({
  pageId,
  rowsPerPage,
  filteredResults,
  page,
  isGroupView,
  selectedId,
  selectedIdKey,
  handleRowSelected,
  columns,
  isShowDashIfValueEmpty,
  isLastColumnSticky,
  commonARIALabel,
}) => {
  const paginatedRows =
    rowsPerPage > 0
      ? filteredResults.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        )
      : filteredResults;

  return (
    <TableBody id={`${pageId}-tableBody`}>
      {paginatedRows.map((row, key) => {
        const classNameForMultipleEntries = hasMultipleNestedArrays(
          row,
          isGroupView,
        )
          ? "cellWithMultipleEntries"
          : "";

        const classNameEdit =
          selectedId && row[selectedIdKey] !== selectedId ? "editMode" : "";

        return (
          <TableRow
            {...(selectedId &&
              row[selectedIdKey] === selectedId && {
                selected: true,
              })}
            id={`${pageId}-tableRow-${key + 1}`}
            key={`${pageId}-tableRow-${key + 1}`}
            className={`${classNameEdit} ${classNameForMultipleEntries} `}
            hover
            sx={{
              cursor: "pointer",
              "&:last-child td, &:last-child th": { border: 0 },
            }}
            onClick={() => handleRowSelected?.(row)}
          >
            {columns.map((column, index) => {
              const isLastColumn = columns.length === index + 1;
              let cellContent;
              if (column.render) {
                cellContent = column.render(row);
              } else if (isShowDashIfValueEmpty) {
                cellContent = row[column.id] || EMPTY_VALUE_DASH;
              } else {
                cellContent = row[column.id];
              }
              return (
                <TableCell
                  role="none"
                  colSpan={column.colSpan ?? 1}
                  key={column.id}
                  align={column.align}
                  className={
                    isLastColumnSticky && isLastColumn ? "pinned-right" : ""
                  }
                >
                  {isString(cellContent) ? (
                    <MuiEllipseLabel
                      text={cellContent}
                      labelTypoVariant="h3"
                      labelTypoClassName={"row-label-value"}
                    />
                  ) : (
                    <MuiTypography component={"label"} variant="h3">
                      {cellContent}
                    </MuiTypography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default MuiTableBody;
